/**
 * Peril Registry — Single Source of Truth
 *
 * Internal peril IDs use R-Server PascalCase names directly,
 * eliminating the need for any mapping layer.
 */

export const PERIL_IDS = ['Deficit', 'Excess', 'Germination', 'PoorRangelands'] as const;
export type PerilId = typeof PERIL_IDS[number];

export interface PerilDefinition {
  id: PerilId;
  displayName: string;
  triggerKey: string;
  unit: string;
}

export const PERIL_REGISTRY: Record<PerilId, PerilDefinition> = {
  Deficit: {
    id: 'Deficit',
    displayName: 'Drought / Deficit',
    triggerKey: 'deficit_trigger',
    unit: 'mm (cumulative)',
  },
  Excess: {
    id: 'Excess',
    displayName: 'Excess Rain / Flooding',
    triggerKey: 'excess_trigger',
    unit: 'mm/pentad',
  },
  Germination: {
    id: 'Germination',
    displayName: 'Dry Spell / Germination',
    triggerKey: 'germination_trigger',
    unit: 'mm',
  },
  PoorRangelands: {
    id: 'PoorRangelands',
    displayName: 'Poor Rangelands',
    triggerKey: 'poor_rangelands_trigger',
    unit: 'zFPARc',
  },
};

export function isPerilId(value: string): value is PerilId {
  return PERIL_IDS.includes(value as PerilId);
}

export function getPerilDisplayName(perilId: string): string {
  if (isPerilId(perilId)) {
    return PERIL_REGISTRY[perilId].displayName;
  }
  return perilId;
}

export function getPerilTriggerKey(perilId: string): string {
  if (isPerilId(perilId)) {
    return PERIL_REGISTRY[perilId].triggerKey;
  }
  return `${perilId.toLowerCase()}_trigger`;
}

export function getPerilUnit(perilId: string): string {
  if (isPerilId(perilId)) {
    return PERIL_REGISTRY[perilId].unit;
  }
  return '';
}
